 import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Avatar,
    TablePagination,
  CircularProgress,
  Alert,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
    PictureAsPdf as PdfIcon,
  RequestQuote as RequestQuoteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useOrder } from '../../contexts/OrderContext';
import { useLocation } from '../../contexts/LocationContext';
import NewOrderModal from '../../components/orders/NewOrderModal';
import PaymentHistory from '../../components/orders/PaymentHistory';
import api from '../../utils/apiConfig';
import EditOrderModal from '../../components/orders/EditOrderModal';
import ImportExportButtons from '../../components/common/ImportExportButtons';
import { exportToCSV, downloadCSV, parseCSV, readFileAsText, formatDateForCSV, formatCurrencyForCSV } from '../../utils/csvUtils';
import { useTheme, useMediaQuery } from '@mui/material';

/** Treat as quote when API sends orderType QUOTE or orderNumber looks like QT12345 */
function isQuoteDocument(o) {
  if (!o) return false;
  const t = String(o.orderType ?? o.order_type ?? '').toUpperCase();
  if (t === 'QUOTE') return true;
  const num = String(o.orderNumber ?? o.order_no ?? '').trim();
  return /^QT\d+/i.test(num);
}

const Orders = () => {
  const {
    orders,
    loading,
    error,
    pagination,
    updateFilters,
    changePage,
    getOrder,
    deleteOrder,
    clearError
  } = useOrder();
  const { selectedLocation } = useLocation();
  
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null); // full order object for dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [locationUsers, setLocationUsers] = useState([]);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [sendInvoiceMsg, setSendInvoiceMsg] = useState(null);
  const [sendInvoiceError, setSendInvoiceError] = useState(null);

  // CSV Export Handler - exports ALL orders from backend
  const handleExport = async () => {
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('Please select a location first');
      return;
    }

    try {
      const response = await api.get(`/orders/export?locationId=${locationId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export orders');
    }
  };

  // CSV Import Handler
  const handleImport = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', selectedLocation?.id || selectedLocation?._id || '');

      const response = await api.post('/orders/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert(response.data.message);
        // Reload orders
        updateFilters({});
      } else {
        alert(`Import failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    }
  };

  // Fetch users for this location
  const fetchLocationUsers = useCallback(async () => {
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) return;
    
    try {
      const response = await api.get(`/users/location/${locationId}`);
      if (response.data.success) {
        setLocationUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch location users:', error);
    }
  }, [selectedLocation]);

  useEffect(() => {
    fetchLocationUsers();
  }, [fetchLocationUsers]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      updateFilters({ search: searchTerm, status: statusFilter, createdById: userFilter, orderType: orderTypeFilter });
    }, 500);
    
    return () => clearTimeout(delayedSearch);
  }, [searchTerm, statusFilter, userFilter, orderTypeFilter, updateFilters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Map backend/enum statuses to friendly labels for display
  const mapStatusLabel = (status) => {
    if (!status) return '';
    const raw = String(status).toUpperCase().replace(/_/g, '_');
    const enumToFriendly = {
      PENDING: 'Pending',
      PROCESSING: 'Processing',
      ITEMS_ORDERED: 'Items Ordered',
      ITEMS_ARRIVED: 'Items Arrived',
      DELIVERED: 'Delivered',
      COMPLETE: 'Complete',
      CANCELLED: 'Cancelled',
      REFUNDED: 'Refunded',
      // Legacy mappings for backwards compatibility
      CONFIRMED: 'Complete',
      SHIPPED: 'Items Arrived',
      COMPLETED: 'Complete',
    };
    return enumToFriendly[raw] || String(status).replace(/_/g, ' ').toLowerCase();
  };

  const getStatusColor = (status) => {
    const s = mapStatusLabel(status);
    switch (s) {
      case 'Pending': return 'warning';
      case 'Processing': return 'warning';
      case 'Items Ordered': return 'info';
      case 'Items Arrived': return 'info';
      case 'Delivered': return 'success';
      case 'Complete': return 'success';
      case 'Cancelled': return 'error';
      case 'Refunded': return 'error';
      default: return 'default';
    }
  };

  const handleSendInvoice = async () => {
    const order = getSelectedOrder();
    const id = getOrderId(order);
    if (!id) return;
    setSendingInvoice(true);
    setSendInvoiceMsg(null);
    setSendInvoiceError(null);
    try {
      const res = await api.post(`/orders/${id}/send-invoice`);
      setSendInvoiceMsg(res?.data?.message || 'Invoice sent successfully');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to send invoice';
      setSendInvoiceError(msg);
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleViewDetailsClick = async () => {
    const orderToView = getSelectedOrder();
    if (orderToView) {
      const id = getOrderId(orderToView);
      try {
        const full = await getOrder(id);
        setSelectedOrder(full || orderToView);
        setDetailsOpen(true);
      } catch (e) {
        // fallback to list order if fetch fails
        setSelectedOrder(orderToView);
        setDetailsOpen(true);
      }
    }
  };

  const handleEditClick = async () => {
    const orderToEdit = getSelectedOrder();
    if (orderToEdit) {
      const id = getOrderId(orderToEdit);
      try {
        const full = await getOrder(id);
        setSelectedOrder(full || orderToEdit);
        setEditOpen(true);
      } catch (e) {
        // fallback to list order if fetch fails
        setSelectedOrder(orderToEdit);
        setEditOpen(true);
      }
    }
  };

  const handleDeleteClick = () => {
    const orderToDelete = getSelectedOrder();
    if (orderToDelete) {
      handleDelete(orderToDelete);
    }
  };

  const handleDelete = async (order) => {
    const id = getOrderId(order);
    if (!id) {
      console.error('Delete failed: Order ID is undefined', { order });
      return;
    }
    try {
      console.debug('Deleting order', { id });
      await deleteOrder(id);
    } catch (error) {
      console.error('Failed to delete order:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    changePage(newPage + 1);
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'N/A';
    if (customer.type === 'business') {
      return customer.businessName;
    }
    return `${customer.firstName} ${customer.lastName}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Build printable invoice HTML and open print dialog
  const openInvoicePdf = async (orderArg) => {
    if (!orderArg) return;
    let order = orderArg;
    const idFresh = getOrderId(orderArg);
    if (idFresh) {
      try {
        const res = await api.get(`/orders/${idFresh}`);
        const full = res?.data?.data?.order;
        if (full) order = full;
      } catch (_) {
        /* keep list/detail snapshot */
      }
    }

    // Fetch payment history for layaway orders
    let payments = [];
    const isLayaway = (order.paymentMethod || '').toUpperCase() === 'LAYAWAY';
    if (isLayaway) {
      try {
        const res = await api.get(`/orders/${getOrderId(order)}/payments`);
        if (res.data?.success) {
          payments = res.data.data?.payments || [];
        }
      } catch (err) {
        console.error('Failed to load payments for invoice:', err);
      }
    }
    const customerName = getCustomerName(order.customer) || '';
    const addr = order.shippingAddress || order.customer?.address || {};
    const items = (order.orderItems || order.items || []).map(i => ({
      name: i.name || i.inventory?.name || 'Item',
      sku: i.sku || i.inventory?.sku || '',
      qty: i.quantity || 0,
      taxRate: Number(i.taxRate || order.pricing?.tax?.rate || 7.5),
      unit: Number(i.unitPrice || 0),
      originalPrice: Number(i.originalPrice || i.unitPrice || 0),
      total: Number(i.totalPrice || 0),
      image: i.image || i.inventory?.images?.[0] || null
    }));
    const subtotal = Number(order.subtotal ?? order.pricing?.subtotal ?? 0);
    const tax = Number(order.taxAmount ?? order.pricing?.tax?.amount ?? 0);
    const total = Number(order.totalAmount ?? order.pricing?.total ?? 0);
    const paid = Number(order.paidAmount ?? order.payment?.amountPaid ?? total);
    const amountDue = total - paid;
    const totalItemCount = items.reduce((sum, i) => sum + i.qty, 0);
    const orderDate = order.orderDate || order.createdAt;
    const paymentMethod = order.paymentMethod || order.payment?.method || 'N/A';
    const paymentStatus = (order.paymentStatus || order.payment?.status || 'Pending').toString();
    const notes = order.notes || order.specialInstructions || '';
    
    // Get delivery/shipping fee
    const shippingAmount = Number(
      order.shippingAmount ?? 
      order.pricing?.extras?.shippingAmount ?? 
      order.pricing?.shippingExtra ?? 
      order.shippingExtraAmount ?? 0
    );
    const deliveryType = (order.shippingMethod || order.delivery?.type || 'shipping').toLowerCase();
    let deliveryFeeLabel = 'Shipping';
    if (deliveryType.includes('delivery')) {
      deliveryFeeLabel = 'Delivery Fee';
    } else if (deliveryType.includes('pickup')) {
      deliveryFeeLabel = 'Pickup Fee';
    }
    
    // Get warranty fee
    const warrantyAmount = Number(
      order.warrantyAmount ?? 
      order.pricing?.extras?.warrantyAmount ?? 
      order.pricing?.warrantyExtra ?? 
      order.warrantyExtraAmount ?? 0
    );

    const currency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
    const formatPercent = (n) => `${Number(n || 0).toFixed(2)}%`;
    const isQuote = isQuoteDocument(order);
    const pageTitle = isQuote ? `New quote ${order.orderNumber}` : `Invoice ${order.orderNumber}`;
    const mainTitle = isQuote ? 'NEW QUOTE' : 'INVOICE';
    const toLabel = isQuote ? 'QUOTE FOR' : 'BILL TO';
    const numberLabel = isQuote ? 'QUOTE NO' : 'INVOICE NO';
    const validThroughRow = isQuote && order.expectedDate
      ? `<div class="meta-row">
      <div class="meta-label">VALID THROUGH</div>
      <div class="meta-value">${new Date(order.expectedDate).toLocaleDateString()}</div>
    </div>`
      : '';
    const paymentMethodRow = isQuote
      ? ''
      : `<div class="meta-row">
      <div class="meta-label">PAYMENT METHOD</div>
      <div class="meta-value">${paymentMethod.toUpperCase()}</div>
    </div>`;
    const leftStatusBlock = isQuote
      ? `<div class="status-row">
        <div class="status-label">STATUS:</div>
        <div class="status-value" style="color:#0d47a1;font-weight:bold;">Furniture price quotation (not a bill or invoice)</div>
      </div>`
      : `<div class="status-row">
        <div class="status-label">PAYMENT STATUS:</div>
        <div class="status-value ${paymentStatus.toLowerCase() === 'paid' ? 'status-paid' : 'status-pending'}">${paymentStatus}</div>
      </div>`;
    const paidAndDueBlock = isQuote
      ? ''
      : `<tr>
          <td class="label">PAID:</td>
          <td class="value">${currency(paid)}</td>
        </tr>
        <tr class="due-row">
          <td class="label">AMOUNT DUE:</td>
          <td class="value">${currency(amountDue)}</td>
        </tr>`;
    const totalRowLabel = isQuote ? 'ESTIMATED TOTAL' : 'TOTAL';
    const taxLabelPrint = isQuote ? 'TAX (EST.):' : 'TAX:';
    const printFooterBlock = isQuote
      ? `<div class="footer-text">
      <strong>NEW QUOTE</strong> &mdash; This is a price quotation for furniture and related products. It is <strong>not a tax invoice</strong>.<br>
      Prices, lead times, and availability are estimates until a firm order is placed.       This quote is valid through the &quot;Valid through&quot; date above unless withdrawn earlier.<br>
      Thank you for the opportunity to quote your business.
    </div>`
      : `<div class="footer-text">
      ${isLayaway ? `
      <strong>LAYAWAY TERMS:</strong> This is a layaway order. Items will be held until full payment is received.<br>
      Balance Due: ${currency(amountDue)}<br><br>
      ` : ''}
      Thank you for your valued business. We greatly value your trust<br>
      and confidence and sincerely appreciate your loyalty to our business.<br>
      In order to keep our prices low
    </div>`;

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${pageTitle}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20px; }
    
    /* Header Section */
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #0066cc; padding-bottom: 15px; }
    .invoice-title { font-size: 28px; font-weight: bold; color: #0066cc; margin-bottom: 15px; }
    .header-left { }
    .customer-info { }
    .info-label { font-size: 10px; color: #666; font-weight: bold; margin-bottom: 5px; }
    .info-name { font-weight: bold; font-size: 14px; }
    .info-details { font-size: 11px; line-height: 1.4; }
    .company-info { text-align: right; }
    .company-logo { max-width: 150px; max-height: 60px; object-fit: contain; margin-bottom: 10px; }
    .company-name { font-weight: bold; font-size: 16px; margin-bottom: 5px; }
    .company-details { font-size: 11px; line-height: 1.4; }
    
    /* Meta Info Section */
    .meta-section { margin-bottom: 20px; }
    .meta-row { display: flex; margin-bottom: 4px; }
    .meta-label { font-weight: bold; color: #0066cc; width: 140px; }
    .meta-value { }
    
    /* Items Table */
    .items-section { margin-bottom: 20px; }
    .items-table { width: 100%; border-collapse: collapse; }
    .items-table th { 
      background: #0066cc; 
      color: white; 
      padding: 10px 8px; 
      text-align: left; 
      font-size: 11px;
      font-weight: bold;
    }
    .items-table th.right { text-align: right; }
    .items-table td { 
      padding: 12px 8px; 
      border-bottom: 1px solid #ddd; 
      vertical-align: top;
    }
    .items-table td.right { text-align: right; }
    .item-row { }
    .item-cell { display: flex; align-items: flex-start; gap: 10px; }
    .item-image { width: 50px; height: 50px; object-fit: cover; border: 1px solid #ddd; }
    .item-image-placeholder { width: 50px; height: 50px; background: #f0f0f0; border: 1px solid #ddd; }
    .item-details { }
    .item-name { font-weight: bold; font-size: 11px; }
    .item-desc { font-size: 10px; color: #666; }
    .original-price { text-decoration: line-through; color: #999; font-size: 10px; }
    
    /* Bottom Section - Two Columns */
    .bottom-section { display: flex; justify-content: space-between; gap: 40px; }
    .left-column { flex: 1; }
    .right-column { width: 280px; }
    
    /* Payment Status & Notes */
    .status-row { display: flex; margin-bottom: 8px; align-items: center; }
    .status-label { font-weight: bold; width: 140px; }
    .status-value { }
    .status-paid { color: #28a745; font-weight: bold; }
    .status-pending { color: #dc3545; font-weight: bold; }
    .note-box { margin-top: 10px; }
    .note-label { font-weight: bold; color: #0066cc; }
    .note-content { 
      margin-top: 5px; 
      padding: 10px; 
      border: 1px solid #ddd; 
      min-height: 60px; 
      background: #fafafa;
    }
    
    /* Totals Table */
    .totals-table { width: 100%; border-collapse: collapse; }
    .totals-table td { padding: 8px 10px; border-bottom: 1px solid #ddd; }
    .totals-table .label { font-weight: bold; }
    .totals-table .value { text-align: right; font-weight: bold; }
    .totals-table .total-row { background: #f5f5f5; }
    .totals-table .total-row td { font-size: 14px; }
    .totals-table .due-row td { color: #dc3545; }
    
    /* Footer */
    .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; }
    .footer-title { font-weight: bold; margin-bottom: 5px; }
    .footer-text { font-size: 10px; color: #666; line-height: 1.4; }
    .all-sales-final { margin-top: 10px; font-size: 15px; font-weight: bold; letter-spacing: 0.5px; color: #111; text-align: center; text-transform: uppercase; }
    
    @media print {
      body { padding: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <div class="invoice-title">${mainTitle}</div>
      <div class="customer-info">
        <div class="info-label">${toLabel}</div>
        <div class="info-name">${customerName.toUpperCase()}</div>
        <div class="info-details">
          ${order.customer?.email ? `${order.customer.email}<br>` : ''}
          ${addr.street ? `${addr.street}<br>` : ''}
          ${addr.city ? `${addr.city}, ${addr.state || ''} ${addr.zipCode || ''}<br>` : ''}
          ${order.customer?.phone || ''}
        </div>
      </div>
    </div>
    <div class="company-info">
      ${order.location?.settings?.logo ? `<img src="${order.location.settings.logo}" alt="Logo" class="company-logo">` : ''}
      <div class="company-name">${order.location?.name || 'Company Name'}</div>
      <div class="company-details">
        ${order.location?.address?.street || order.location?.street ? `${order.location?.address?.street || order.location?.street}<br>` : ''}
        ${order.location?.address?.city || order.location?.city ? `${order.location?.address?.city || order.location?.city}, ${order.location?.address?.state || order.location?.state || ''} ${order.location?.address?.zipCode || order.location?.zipCode || ''}<br>` : ''}
        ${order.location?.phone ? `${order.location.phone}<br>` : ''}
        ${order.location?.email || ''}
      </div>
    </div>
  </div>

  <!-- Meta Info -->
  <div class="meta-section">
    <div class="meta-row">
      <div class="meta-label">${numberLabel}</div>
      <div class="meta-value">${order.orderNumber}</div>
    </div>
    <div class="meta-row">
      <div class="meta-label">ISSUE DATE</div>
      <div class="meta-value">${new Date(orderDate).toLocaleString()}</div>
    </div>
    ${validThroughRow}
    ${paymentMethodRow}
  </div>

  <!-- Items Table -->
  <div class="items-section">
    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 35%">ITEM</th>
          <th style="width: 15%">SKU</th>
          <th class="right" style="width: 8%">QTTY</th>
          <th class="right" style="width: 10%">TAX</th>
          <th class="right" style="width: 16%">UNIT PRICE</th>
          <th class="right" style="width: 16%">TOTAL</th>
        </tr>
      </thead>
      <tbody>
        ${items.map(i => `
          <tr class="item-row">
            <td>
              <div class="item-cell">
                ${i.image ? `<img src="${i.image}" class="item-image" alt="">` : '<div class="item-image-placeholder"></div>'}
                <div class="item-details">
                  <div class="item-name">${i.name}</div>
                </div>
              </div>
            </td>
            <td>${i.sku}</td>
            <td class="right">${i.qty}</td>
            <td class="right">${formatPercent(i.taxRate)}</td>
            <td class="right">
              ${i.originalPrice > i.unit ? `<div class="original-price">${currency(i.originalPrice)}</div>` : ''}
              ${currency(i.unit)}
            </td>
            <td class="right">${currency(i.total)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>

  <!-- Bottom Section -->
  <div class="bottom-section">
    <!-- Left Column: Payment Status & Notes -->
    <div class="left-column">
      ${leftStatusBlock}
      <div class="note-box">
        <div class="note-label">NOTE:</div>
        <div class="note-content">${notes || ''}</div>
      </div>
    </div>

    <!-- Right Column: Totals -->
    <div class="right-column">
      <table class="totals-table">
        <tr>
          <td class="label">SUB TOTAL:</td>
          <td class="value">${currency(subtotal)}</td>
        </tr>
        <tr>
          <td class="label">${taxLabelPrint}</td>
          <td class="value">${currency(tax)}</td>
        </tr>
        ${shippingAmount > 0 ? `
        <tr>
          <td class="label">${deliveryFeeLabel.toUpperCase()}:</td>
          <td class="value">${currency(shippingAmount)}</td>
        </tr>
        ` : ''}
        ${warrantyAmount > 0 ? `
        <tr>
          <td class="label">WARRANTY:</td>
          <td class="value">${currency(warrantyAmount)}</td>
        </tr>
        ` : ''}
        <tr class="total-row">
          <td class="label">${totalRowLabel}:</td>
          <td class="value">${currency(total)}</td>
        </tr>
        ${paidAndDueBlock}
        <tr>
          <td class="label">TOTAL ITEM COUNT:</td>
          <td class="value">${totalItemCount}</td>
        </tr>
      </table>
    </div>
  </div>

  ${!isQuote && isLayaway && payments.length > 0 ? `
  <!-- Payment History for Layaway -->
  <div class="payment-history" style="margin-top: 20px; border-top: 2px solid #0066cc; padding-top: 15px;">
    <div style="font-size: 14px; font-weight: bold; color: #0066cc; margin-bottom: 10px;">LAYAWAY PAYMENT HISTORY</div>
    <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
      <thead>
        <tr style="background: #f5f5f5;">
          <th style="padding: 8px; text-align: left; border-bottom: 1px solid #ddd;">Date</th>
          <th style="padding: 8px; text-align: left; border-bottom: 1px solid #ddd;">Method</th>
          <th style="padding: 8px; text-align: right; border-bottom: 1px solid #ddd;">Amount</th>
          <th style="padding: 8px; text-align: left; border-bottom: 1px solid #ddd;">Reference</th>
        </tr>
      </thead>
      <tbody>
        ${payments.map(p => `
        <tr>
          <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">${new Date(p.receivedAt || p.createdAt).toLocaleDateString()}</td>
          <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">${p.method || 'N/A'}</td>
          <td style="padding: 6px 8px; border-bottom: 1px solid #eee; text-align: right;">${currency(p.amount)}</td>
          <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">${p.reference || '-'}</td>
        </tr>
        `).join('')}
      </tbody>
      <tfoot>
        <tr style="font-weight: bold; background: #f9f9f9;">
          <td colspan="2" style="padding: 8px;">Total Payments</td>
          <td style="padding: 8px; text-align: right;">${currency(payments.reduce((sum, p) => sum + Number(p.amount || 0), 0))}</td>
          <td style="padding: 8px;"></td>
        </tr>
      </tfoot>
    </table>
  </div>
  ` : ''}

  <!-- Footer -->
  <div class="footer">
    <div class="footer-title">Terms & Notes</div>
    ${printFooterBlock}
    ${!isQuote ? '<div class="all-sales-final">All Sales Final</div>' : ''}
  </div>

  <script>
    window.onload = function(){ setTimeout(function(){ window.print(); }, 200); };
  </script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      alert('Popup blocked. Please allow popups for this site to print.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.close(); } catch (e) {} }, 5000);
  };

  const getOrderId = (order) => order?._id || order?.id || order?.orderId || null;

  const getSelectedOrder = () => {
    if (selectedOrder) return selectedOrder;
    if (!selectedOrderId) return null;
    return orders.find(o => getOrderId(o) === selectedOrderId) || null;
  };

  const handleOrderCreated = async (createdOrder) => {
    const id = getOrderId(createdOrder);
    if (!id) return;
    try {
      const full = await getOrder(id);
      setSelectedOrder(full || createdOrder);
    } catch (e) {
      setSelectedOrder(createdOrder);
    } finally {
      setSelectedOrderId(id);
      setDetailsOpen(true);
      setNewOrderOpen(false);
      setNewQuoteOpen(false);
    }
  };

  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        mb: 4 
      }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Orders &amp; quotes
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <ImportExportButtons
            onExport={handleExport}
            onImport={handleImport}
            loading={loading}
          />
          <Button
            variant="outlined"
            startIcon={<RequestQuoteIcon />}
            size="large"
            sx={{ fontWeight: 'bold' }}
            onClick={() => setNewQuoteOpen(true)}
          >
            New quote
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            sx={{ fontWeight: 'bold' }}
            onClick={() => setNewOrderOpen(true)}
          >
            New order
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <TextField
              placeholder="Search orders by ID, customer name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Document</InputLabel>
              <Select
                value={orderTypeFilter}
                label="Document"
                onChange={(e) => setOrderTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SALE">Orders only</MenuItem>
                <MenuItem value="QUOTE">Quotes only</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PROCESSING">Processing</MenuItem>
                <MenuItem value="ITEMS_ORDERED">Items Ordered</MenuItem>
                <MenuItem value="ITEMS_ARRIVED">Items Arrived</MenuItem>
                <MenuItem value="DELIVERED">Delivered</MenuItem>
                <MenuItem value="COMPLETE">Complete</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Created By</InputLabel>
              <Select
                value={userFilter}
                label="Created By"
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <MenuItem value="">All Users</MenuItem>
                {locationUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{
              overflowX: 'auto',
              '& table': {
                minWidth: { xs: 650, sm: 750 }
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading orders...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => {
                    const customerName = getCustomerName(order.customer);
                    return (
                      <TableRow key={getOrderId(order)} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {order.orderNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                              {customerName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Chip
                            size="small"
                            label={isQuoteDocument(order) ? 'Quote' : 'Order'}
                            color={isQuoteDocument(order) ? 'secondary' : 'default'}
                            variant={isQuoteDocument(order) ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, width: 40, height: 40, backgroundColor: 'primary.main' }}>
                              {customerName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {customerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.customer.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2">
                            {(order.orderItems || order.items || []).length} items
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(order.totalAmount ?? order.pricing?.total ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={mapStatusLabel(order.status)}
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2">
                            {order.createdBy ? `${order.createdBy.firstName} ${order.createdBy.lastName}` : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2">
                            {new Date(order.orderDate || order.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedOrderId(getOrderId(order));
                                setDetailsOpen(true);
                              }}
                              title="View Details"
                              sx={{ color: 'primary.main' }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedOrderId(getOrderId(order));
                                setEditOpen(true);
                              }}
                              title="Edit Order"
                              sx={{ color: 'warning.main' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this order?')) {
                                  deleteOrder(getOrderId(order));
                                }
                              }}
                              title="Delete Order"
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={pagination.totalItems}
            rowsPerPage={pagination.itemsPerPage}
            page={pagination.currentPage - 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={() => {}}
          />
        </CardContent>
      </Card>

      
      {detailsOpen && getSelectedOrder() && (
      <Dialog 
        open={detailsOpen} 
        onClose={() => { setDetailsOpen(false); setSelectedOrder(null); setSelectedOrderId(null); }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {isQuoteDocument(getSelectedOrder()) ? 'Quote details' : 'Order details'}
          {` — ${getSelectedOrder()?.orderNumber || ''}`}
        </DialogTitle>
        <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>{getCustomerName(getSelectedOrder().customer)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>{getSelectedOrder().customer.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>{getSelectedOrder().customer.phone}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary', mt: 0.5 }} />
                  <Typography>
                    {(() => {
                      const o = getSelectedOrder();
                      const addr = o.shippingAddress || o.customer?.address || {};
                      const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean);
                      return parts.length ? parts.join(', ') : 'No address provided';
                    })()}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Order Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={mapStatusLabel(getSelectedOrder().status)}
                    color={getStatusColor(getSelectedOrder().status)}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Order Date</Typography>
                  <Typography>{formatDate(getSelectedOrder().orderDate || getSelectedOrder().createdAt)}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                  <Chip
                    label={(getSelectedOrder().paymentStatus || getSelectedOrder().payment?.status || 'PENDING').toString().toLowerCase()}
                    color={((getSelectedOrder().paymentStatus || getSelectedOrder().payment?.status || 'PENDING').toString().toLowerCase() === 'paid') ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Delivery Type</Typography>
                  <Typography>{getSelectedOrder().shippingMethod || getSelectedOrder().delivery?.type || 'N/A'}</Typography>
                </Box>
                {getSelectedOrder().delivery?.scheduledDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Scheduled Delivery</Typography>
                    <Typography>{formatDate(getSelectedOrder().delivery.scheduledDate)}</Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Order Items
                </Typography>
                {sendInvoiceMsg && (
                  <Alert severity="success" sx={{ mb: 2 }}>{sendInvoiceMsg}</Alert>
                )}
                {sendInvoiceError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{sendInvoiceError}</Alert>
                )}
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="center">Received</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(getSelectedOrder().orderItems || getSelectedOrder().items || []).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name || item.inventory?.name || 'N/A'}</TableCell>
                          <TableCell>{item.sku || item.inventory?.sku || 'N/A'}</TableCell>
                          <TableCell align="center">
                            {item.arrived ? (
                              <Chip 
                                label={item.arrivedAt ? new Date(item.arrivedAt).toLocaleDateString() : 'Yes'} 
                                color="success" 
                                size="small" 
                              />
                            ) : (
                              <Chip label="Pending" color="warning" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.totalPrice)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(getSelectedOrder().subtotal ?? getSelectedOrder().pricing?.subtotal ?? 0)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>Tax</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(getSelectedOrder().taxAmount ?? getSelectedOrder().pricing?.tax?.amount ?? 0)}
                        </TableCell>
                      </TableRow>
                      {(() => {
                        const o = getSelectedOrder();
                        const subtotal = Number(o.subtotal ?? o.pricing?.subtotal ?? 0);
                        const tax = Number(o.taxAmount ?? o.pricing?.tax?.amount ?? 0);
                        const total = Number(o.totalAmount ?? o.pricing?.total ?? 0);

                        // Prefer server-provided amounts; fall back to legacy client fields
                        let shipping = Number(
                          o.shippingAmount ??
                          o.pricing?.extras?.shippingAmount ??
                          o.pricing?.shippingExtra ??
                          o.shippingExtraAmount ?? 0
                        );

                        let warranty = Number(
                          o.warrantyAmount ??
                          o.pricing?.extras?.warrantyAmount ??
                          o.pricing?.warrantyExtra ??
                          o.warrantyExtraAmount ?? 0
                        );

                        // If no explicit warranty is present, infer it from remainder so the breakdown matches total
                        if (!isFinite(warranty) || warranty <= 0) {
                          const inferred = total - (subtotal + tax + shipping);
                          // Only use positive, sensible remainder as warranty
                          warranty = inferred > 0 ? inferred : 0;
                        }

                        // Determine the correct fee label based on delivery type
                        const deliveryType = (o.shippingMethod || o.delivery?.type || 'shipping').toLowerCase();
                        let feeLabel = 'Shipping';
                        if (deliveryType.includes('delivery')) {
                          feeLabel = 'Delivery Fee';
                        } else if (deliveryType.includes('pickup')) {
                          feeLabel = 'Pickup Fee';
                        } else if (deliveryType.includes('shipping')) {
                          feeLabel = 'Shipping';
                        }

                        return (
                          <>
                            <TableRow>
                              <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>{feeLabel}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(shipping)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>Warranty</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(warranty)}
                              </TableCell>
                            </TableRow>
                          </>
                        );
                      })()}
                      <TableRow>
                        <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(getSelectedOrder().totalAmount ?? getSelectedOrder().pricing?.total ?? 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Payment History Section */}
              <Grid item xs={12}>
                <PaymentHistory 
                  orderId={getOrderId(getSelectedOrder())}
                  order={getSelectedOrder()}
                  onPaymentRecorded={() => {
                    // Refresh order data when payment is recorded
                    const id = getOrderId(getSelectedOrder());
                    if (id) {
                      getOrder(id).then(full => {
                        if (full) setSelectedOrder(full);
                      });
                    }
                  }}
                />
              </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={() => openInvoicePdf(getSelectedOrder())}>
            {isQuoteDocument(getSelectedOrder()) ? 'Print quote' : 'View PDF'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendInvoice}
            disabled={sendingInvoice}
          >
            {sendingInvoice ? 'Sending...' : (isQuoteDocument(getSelectedOrder()) ? 'Send quote' : 'Send invoice')}
          </Button>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      )}

      {editOpen && getSelectedOrder() && (
        <EditOrderModal
          open={editOpen}
          onClose={() => { setEditOpen(false); setSelectedOrder(null); setSelectedOrderId(null); }}
          order={getSelectedOrder()}
        />
      )}

      <NewOrderModal 
        key="new-order-modal"
        open={newOrderOpen} 
        onClose={() => setNewOrderOpen(false)} 
        onCreated={handleOrderCreated}
        mode="order"
      />
      <NewOrderModal
        key="new-quote-modal"
        open={newQuoteOpen}
        onClose={() => setNewQuoteOpen(false)}
        onCreated={handleOrderCreated}
        mode="quote"
      />
    </Box>
  );
};

export default Orders;
